import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarCheck, Zap, Users, Wind } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { LeadForm } from "../components/LeadForm";
import { EnergyArc } from "../components/MovementViz";
import { PROGRAMS } from "../data/content";
import { ASSETS } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#548a88";
const DRUM_CIRCLE_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_trauma-healing-tech/artifacts/cvdlte0p_02AC920C-B21A-48F7-957E-39A098F1B08E.webp";

const SYNERGY = [
  { icon: <Zap size={26} />, title: "Physical Energy Resets", desc: "Rapid transition from sedentary conference seating to active, full-body engagement." },
  { icon: <Users size={26} />, title: "Audience Synchronization", desc: "Converts passive listeners into active co-creators within 60 seconds." },
  { icon: <Wind size={26} />, title: "Stress & Tension Release", desc: "High-impact rhythmic release that breaks down corporate barriers." },
];

const ARC_STAGES = ["Icebreaker Entry", "Synchronized Group Groove", "High-Energy Climax", "Focused Call-to-Action"];

function SynergyVisualizer() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="drumtalk-synergy">
      {SYNERGY.map((s, i) => (
        <motion.div
          key={s.title}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, delay: i * 0.12 }}
          className="glass shape-r p-7 text-center"
          data-testid={`synergy-${i}`}
        >
          <div className="beat-pulse grid place-items-center h-16 w-16 rounded-full mx-auto mb-5" style={{ backgroundColor: `${ACCENT}22`, border: `2px solid ${ACCENT}`, color: ACCENT, "--pulse-color": `${ACCENT}55`, animationDelay: `${i * 0.4}s` }}>
            {s.icon}
          </div>
          <h4 className="font-serif text-xl md:text-2xl">{s.title}</h4>
          <p className="mt-3 text-fg/65 text-[15px] leading-snug">{s.desc}</p>
        </motion.div>
      ))}
    </div>
  );
}

const REEL = [
  { id: "energy", label: "Big-Room Energy", caption: "Hundreds of people, one pulse — a keynote that moves the whole room." },
  { id: "reaction", label: "Audience Reaction", caption: "From arms-crossed to all-in within the first four bars." },
  { id: "testimonial", label: "Testimonials", caption: "\"The most talked-about session of our summit.\" — Conference Organizer" },
];

function HighlightReel() {
  const [tab, setTab] = useState("energy");
  const active = REEL.find((r) => r.id === tab);
  return (
    <div data-testid="drumtalk-reel">
      <div className="flex flex-wrap gap-2 mb-5">
        {REEL.map((r) => (
          <button key={r.id} onClick={() => setTab(r.id)} data-testid={`reel-tab-${r.id}`} className="shape-r px-4 py-2.5 font-mono text-[10px] tracking-[0.15em] uppercase transition-colors" style={{ border: `1px solid ${ACCENT}55`, backgroundColor: tab === r.id ? ACCENT : "transparent", color: tab === r.id ? "#141b1d" : "var(--fg)" }}>{r.label}</button>
        ))}
      </div>
      <div className="shape-r overflow-hidden border border-white/10 relative">
        <video key={tab} className="w-full max-h-[56vh] object-cover" autoPlay muted loop playsInline poster={ASSETS.heroFallback}>
          <source src={ASSETS.heroVideo} type="video/mp4" />
        </video>
        <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
          <p className="font-serif text-xl md:text-2xl" data-testid="reel-caption">{active.caption}</p>
        </div>
      </div>
    </div>
  );
}

const DEPOSIT_TIERS = [
  { key: "drumtalk_deposit_local", label: "Local", note: "No travel · within region", amount: "$500" },
  { key: "drumtalk_deposit_domestic", label: "Domestic", note: "In-state · regional travel", amount: "$1,000" },
  { key: "drumtalk_deposit_national", label: "National", note: "Out-of-state · flights", amount: "$1,500" },
];

function DepositReservation() {
  const [form, setForm] = useState({ name: "", email: "", preferred_date: "" });
  const [tierKey, setTierKey] = useState("drumtalk_deposit_local");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const tier = DEPOSIT_TIERS.find((t) => t.key === tierKey);
  const field = "w-full shape-r bg-black/25 border border-white/15 focus:outline-none focus:border-primary-c p-3.5 text-fg placeholder:text-fg/30 transition-colors";
  const reserve = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || !form.preferred_date) {
      setErr("Please add your name, email, and a preferred date.");
      return;
    }
    setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, { lookup_key: tierKey, origin_url: window.location.origin, ...form });
      window.location.href = data.checkout_url;
    } catch (e2) { setErr("Could not start checkout. Please try again."); setBusy(false); }
  };
  return (
    <div className="glass shape-r p-8 md:p-10" data-testid="drumtalk-deposit">
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50">Engagement scope</label>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="deposit-tiers">
        {DEPOSIT_TIERS.map((t) => {
          const on = tierKey === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setTierKey(t.key)} data-testid={`deposit-tier-${t.label.toLowerCase()}`}
              className="shape-r text-left p-4 transition-colors"
              style={{ border: `1px solid ${on ? ACCENT : "rgba(255,255,255,0.15)"}`, backgroundColor: on ? `${ACCENT}1A` : "transparent" }}>
              <div className="flex items-baseline justify-between">
                <span className="font-medium" style={{ color: on ? ACCENT : "var(--fg)" }}>{t.label}</span>
                <span className="font-serif text-lg" style={{ color: on ? ACCENT : "var(--fg)" }}>{t.amount}</span>
              </div>
              <span className="block font-mono text-[9px] tracking-[0.1em] uppercase text-fg/45 mt-1">{t.note}</span>
            </button>
          );
        })}
      </div>
      <div className="flex items-baseline gap-3 mt-6 mb-6">
        <span className="font-serif text-4xl" style={{ color: ACCENT }} data-testid="deposit-amount">{tier.amount}</span>
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50">{tier.label} · date-hold deposit</span>
      </div>
      <form onSubmit={reserve} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input data-testid="deposit-name" className={field} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="deposit-email" className={field} type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50 mb-1.5 block">Preferred event date</label>
          <input data-testid="deposit-date" className={field} type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} />
        </div>
        {err && <p className="text-sm" style={{ color: "#c46a58" }} data-testid="deposit-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="deposit-submit" className="inline-flex items-center gap-2 shape-r font-medium px-7 py-3.5 transition-opacity hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: ACCENT, color: "#141b1d" }}>
          <CalendarCheck size={16} /> {busy ? "Starting checkout…" : `Reserve my date · ${tier.amount}`}
        </button>
        <p className="font-mono text-[10px] tracking-[0.1em] text-fg/40">Secure checkout via Stripe · test card 4242 4242 4242 4242</p>
      </form>
    </div>
  );
}

export default function DrumTalk() {
  const prog = PROGRAMS.find((p) => p.slug === "drumtalk");
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <div className="grain min-h-screen" data-testid="page-drumtalk">
      <Nav />
      <section className="relative pt-36 md:pt-48 pb-16 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0" style={{ background: `radial-gradient(90% 70% at 20% 0%, rgba(84,138,136,0.18), transparent 60%)` }} />
        <div className="relative max-w-6xl mx-auto">
          <Link to="/#programs" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-fg/50 hover:text-fg transition-colors mb-10"><ArrowLeft size={14} /> All Programs</Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <p className="font-mono text-xs tracking-[0.3em] uppercase mb-4" style={{ color: ACCENT }}>For Corporate & Large Groups</p>
              <h1 className="font-serif text-5xl md:text-6xl tracking-tight">DrumTalk</h1>
              <p className="mt-6 max-w-xl text-fg/70 md:text-xl leading-relaxed">{prog.hero}</p>
              <div className="mt-9 flex flex-wrap gap-4">
                <a href="#drumtalk-reserve" data-testid="drumtalk-reserve-top" className="shape-r font-medium px-7 py-3.5 transition-opacity hover:opacity-90" style={{ backgroundColor: ACCENT, color: "#141b1d" }}>Reserve your date</a>
                <a href="#drumtalk-quote" className="shape-r border border-white/25 font-medium px-7 py-3.5 hover:bg-white/10 transition-colors">Request a quote</a>
              </div>
            </div>
            <div className="shape-r overflow-hidden border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.4)]" data-testid="drumtalk-hero-sizzle">
              <video className="w-full aspect-video object-cover" autoPlay muted loop playsInline preload="auto" poster={ASSETS.heroFallback}>
                <source src="/clips/pals-1.mp4" type="video/mp4" />
                <source src="/clips/pals-1.webm" type="video/webm" />
              </video>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-10 pb-4" data-testid="drumtalk-circle-section">
        <div className="max-w-6xl mx-auto">
          <motion.figure
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="shape-r overflow-hidden border border-white/10 relative shadow-[0_20px_60px_rgba(0,0,0,0.4)]"
            data-testid="drumtalk-circle-photo"
          >
            <img
              src={DRUM_CIRCLE_IMG}
              alt="Casey leading a live group drum circle"
              className="w-full max-h-[72vh] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
            <figcaption className="absolute bottom-0 inset-x-0 p-6 md:p-10">
              <p className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase mb-2.5" style={{ color: ACCENT }}>In the room</p>
              <p className="font-serif text-2xl md:text-4xl max-w-2xl leading-tight">
                One leader, a room full of hands — everybody on the same beat.
              </p>
            </figcaption>
          </motion.figure>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Synchronous Movement & Synergy</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-14">How unified rhythm moves a room.</h2>
          <SynergyVisualizer />
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Conference Flow & Energy Arc</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-12">The energy curve of a session.</h2>
          <EnergyArc stages={ARC_STAGES} accent={ACCENT} testid="drumtalk-arc" />
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>See It — Highlight Reel</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-10">The energy, in motion.</h2>
          <HighlightReel />
        </div>
      </section>

      <section id="drumtalk-reserve" className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-3" style={{ color: ACCENT }}><CalendarCheck size={16} /><p className="font-mono text-xs tracking-[0.3em] uppercase">Reserve Your Date</p></div>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-4">Hold your keynote date with a deposit.</h2>
          <p className="text-fg/60 md:text-lg mb-8">Lock in Casey for your event. Your deposit reserves the date and applies to your final invoice.</p>
          <DepositReservation />
        </div>
      </section>

      <section id="drumtalk-quote" className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Fast-Track Quote</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-4">Need numbers for procurement?</h2>
          <p className="text-fg/60 md:text-lg mb-8">Tell us about your event and we'll send a tailored quote — built for corporate timelines.</p>
          <LeadForm
            testid="drumtalk-quote-form" type="quote" page="drumtalk" accent={ACCENT}
            submitLabel="Request my quote"
            selects={[{ name: "event_type", label: "Event type", options: ["Keynote Opener", "Breakout Session", "Half-Day Workshop", "Not sure yet"] }, { name: "timeline", label: "Timeline", options: ["Within 30 days", "1–3 months", "3+ months"] }]}
            successTitle="Quote request received." successBody="Our team will send a tailored quote within one business day."
          />
        </div>
      </section>
      <Footer />
    </div>
  );
}
